// resources/js/src/components/community/components/profile/ProfileHeader.tsx
import React from "react";
import { Icon } from "@iconify/react";
import type { ProfileState } from "../../../../../services/profile.api";
import ProfileActions from "./ProfileActions";

type Props = {
  name: string;
  friendCount: number;
  coverPhoto: string | null;
  profilePicture: string | null;
  state: ProfileState | null;
  userId: string;
  onActionDone?: () => void;
};

export const ProfileHeader: React.FC<Props> = ({
  name,
  friendCount,
  coverPhoto,
  profilePicture,
  state,
  userId,
  onActionDone,
}) => {
  const handleActionDone = onActionDone ?? (() => {});

  return (
    <div className="relative">
      {/* ==================== Cover ==================== */}
      <div className="relative h-48 md:h-72 lg:h-80 overflow-hidden">
        {coverPhoto ? (
          <img
            src={coverPhoto}
            alt={`${name} cover`}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-400 via-primary-500 to-primary-700" />
        )}

        {/* Cover Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Edit Cover (Owner only) */}
        {state === "owner" && (
          <button className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black/50 hover:bg-black/70 text-white text-xs font-medium backdrop-blur-sm transition-all duration-200">
            <Icon icon="mdi:camera-outline" className="text-base" />
            Edit Cover
          </button>
        )}
      </div>

      {/* ==================== Info Bar ==================== */}
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-16 md:-mt-20 relative z-10">
          {/* ==================== Avatar ==================== */}
          <div className="relative group shrink-0">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white dark:border-deepSlate overflow-hidden bg-gray-100 dark:bg-gray-800 shadow-xl shadow-black/10 transition-all duration-300 group-hover:shadow-2xl">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt={name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl md:text-5xl font-bold text-gray-400 dark:text-gray-500 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                  {name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Online Badge (اختياري) */}
            <span className="absolute bottom-2 right-2 w-5 h-5 md:w-6 md:h-6 rounded-full bg-green-500 border-2 border-white dark:border-deepSlate shadow-sm" />

            {/* Edit Avatar (Owner only) */}
            {state === "owner" && (
              <button className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Icon icon="mdi:camera-outline" className="text-white text-2xl" />
              </button>
            )}
          </div>

          {/* ==================== Name + Friends Count ==================== */}
          <div className="flex-1 pb-2 min-w-0">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white truncate">
              {name}
            </h1>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
              <Icon icon="mdi:account-group-outline" className="text-base" />
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {friendCount.toLocaleString()}
              </span>
              friends
            </p>
          </div>

          {/* ==================== Actions ==================== */}
          {state && (
            <div className="pb-2 shrink-0">
              <ProfileActions
                state={state}
                userId={userId}
                userName={name}
                onActionDone={handleActionDone}
              />
            </div>
          )}
        </div>

        {/* ==================== Divider ==================== */}
        <div className="mt-4 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />
      </div>
    </div>
  );
};

export default ProfileHeader;